import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useOnboarding } from "@/hooks/use-onboarding";
import { toast } from "@/hooks/use-toast";

interface TermsStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function TermsStep({ onNext, onBack }: TermsStepProps) {
  const [accepted, setAccepted] = useState(false);
  const { completeStep, isUpdating } = useOnboarding();

  const handleAccept = async () => {
    try {
      await completeStep("has_accepted_terms");
      onNext();
    } catch {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível salvar a aceitação dos termos. Tente novamente.",
      });
    }
  };

  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">Termos de Uso</h2>
        <p className="text-muted-foreground">
          Leia e aceite os termos para continuar
        </p>
      </div>

      <ScrollArea className="h-64 w-full rounded-md border p-4">
        <div className="space-y-4 text-sm text-muted-foreground pr-4">
          <h3 className="font-semibold text-foreground">
            1. Aceitação dos Termos
          </h3>
          <p>
            Ao utilizar o Next AI Finance, você concorda com estes Termos de Uso.
            Se você não concordar com qualquer parte destes termos, não poderá
            utilizar nossos serviços.
          </p>

          <h3 className="font-semibold text-foreground">
            2. Descrição do Serviço
          </h3>
          <p>
            O Next AI Finance é uma plataforma de gestão financeira pessoal que
            permite o controle de contas, transações, orçamentos e metas
            financeiras. O serviço inclui análises baseadas em inteligência
            artificial para auxiliar na tomada de decisões financeiras.
          </p>

          <h3 className="font-semibold text-foreground">
            3. Conta do Usuário
          </h3>
          <p>
            Você é responsável por manter a confidencialidade de sua conta e
            senha. Todas as atividades realizadas em sua conta são de sua
            responsabilidade.
          </p>

          <h3 className="font-semibold text-foreground">
            4. Privacidade e Dados
          </h3>
          <p>
            Seus dados financeiros são armazenados de forma segura e criptografada.
            Não compartilhamos suas informações pessoais ou financeiras com
            terceiros sem seu consentimento expresso.
          </p>

          <h3 className="font-semibold text-foreground">
            5. Uso Adequado
          </h3>
          <p>
            Você concorda em utilizar o serviço apenas para fins legais e de
            acordo com estes termos. É proibido qualquer uso que possa prejudicar
            o funcionamento da plataforma ou outros usuários.
          </p>

          <h3 className="font-semibold text-foreground">
            6. Assinaturas e Pagamentos
          </h3>
          <p>
            Planos pagos são cobrados mensalmente. Você pode cancelar sua
            assinatura a qualquer momento. O cancelamento será efetivado ao
            final do período já pago. Planos com período de teste gratuito
            não serão cobrados durante o período de avaliação.
          </p>

          <h3 className="font-semibold text-foreground">
            7. Limitação de Responsabilidade
          </h3>
          <p>
            O Next AI Finance não é um serviço de consultoria financeira.
            As análises e sugestões fornecidas pela IA são informativas e não
            constituem recomendações de investimento. As decisões financeiras
            são de sua inteira responsabilidade.
          </p>

          <h3 className="font-semibold text-foreground">
            8. Modificações dos Termos
          </h3>
          <p>
            Reservamo-nos o direito de modificar estes termos a qualquer momento.
            Alterações significativas serão comunicadas por e-mail ou notificação
            na plataforma.
          </p>
        </div>
      </ScrollArea>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={accepted}
          onCheckedChange={(checked) => setAccepted(checked === true)}
        />
        <label
          htmlFor="terms"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Li e aceito os Termos de Uso
        </label>
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack} disabled={isUpdating}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button onClick={handleAccept} disabled={!accepted || isUpdating}>
          {isUpdating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            "Próximo"
          )}
        </Button>
      </div>
    </div>
  );
}
